/*
 * The Google half of the map seam. This is the behaviour that shipped before
 * the seam existed, moved behind the shared contract unchanged -- the same
 * gmap-vue components, the same options, the same $gmapApiPromiseLazy bootstrap
 * and the same LatLngBounds fitting.
 *
 * `GmapMap` puts `vue-map-container` on its own root, which is what the
 * Cypress selector and ShowOnMapModal's scoped CSS bind to; the Leaflet
 * implementation reproduces it deliberately.
 */
import { computed, defineComponent, reactive, ref, watch } from '@vue/composition-api';
import Vue from 'vue';
const DEFAULT_MARKER_Z_INDEX = 888;
export default defineComponent({
    props: {
        center: {
            type: Object,
            required: true
        },
        zoom: {
            type: Number,
            default: 16
        },
        markers: {
            type: Array,
            default: () => []
        },
        fitToMarkers: {
            type: Boolean,
            default: false
        },
        selectedMarkerId: {
            type: Number,
            default: null
        }
    },
    setup(props) {
        const mapRef = ref(null);
        const map = ref(null);
        const google = ref(null);
        const mapOptions = reactive({
            zoomControl: true,
            zoomControlOptions: {
                position: null
            },
            scrollwheel: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
            disableDefaultUi: false,
            disableDoubleClickZoom: false
        });
        const selectedMarker = computed(() => props.markers.find(marker => marker.id === props.selectedMarkerId) ||
            null);
        const fitMapBounds = () => {
            if (!google.value || !map.value || props.markers.length < 2)
                return;
            const bounds = new google.value.maps.LatLngBounds();
            props.markers.forEach(marker => bounds.extend(new google.value.maps.LatLng(marker.position.lat, marker.position.lng)));
            map.value.fitBounds(bounds);
        };
        // https://diegoazh.github.io/gmap-vue/#the-three-main-utilities
        Vue.$gmapApiPromiseLazy().then((mapsAPI) => {
            google.value = mapsAPI;
            mapOptions.zoomControlOptions.position =
                mapsAPI.maps.ControlPosition.TOP_LEFT;
        });
        watch(mapRef, () => {
            if (!mapRef.value)
                return;
            mapRef.value.$mapPromise.then(() => {
                map.value = mapRef.value.$mapObject;
                if (props.fitToMarkers)
                    fitMapBounds();
            });
        }, { immediate: true });
        watch(() => props.markers, fitMapBounds);
        return {
            DEFAULT_MARKER_Z_INDEX,
            mapOptions,
            mapRef,
            selectedMarker
        };
    }
});
